/* ============================================================
   Face Scan Funnel controller.
   Owns the 5-step state machine + marketing screens; the real ML scan is the existing
   engine (face-scan-landing.js), reached via window.initFaceScan(root) / root.__faceScanLanding.
   Age is the single hero metric (no sub-scores). Share: native sheet on touch, branded modal on desktop.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var EMAIL_RE = /\S+@\S+\.\S+/;
  /* set from the section's ga4Id config at construction; see FaceScanFunnel() */
  var GA4_ID = '';

  /* Funnel analytics. Three sinks, all consent-safe, none can break the funnel:
     1) GTM dataLayer (consent-gating handled in GTM — see snippets/gtm-head.liquid).
     2) Direct GA4 via gtag send_to, so the fs_* funnel steps reach the GA4 property
        WITHOUT anyone configuring GTM tags. gtag inherits the site's Google consent
        mode, so a denied analytics_storage is respected automatically. send_to pins
        the events to exactly GA4_ID (the store runs >1 GA property).
     3) PostHog, for step-by-step drop-off funnels — the funnel is a single-page app
        with no per-step URL change, so PostHog's autocapture/pageview tracking alone
        cannot reconstruct the funnel; these are the same fs_* event names as (1)/(2).
        window.posthog only exists once snippets/posthog.liquid has loaded it, which it
        only does after Shopify analytics consent is granted — same guard pattern as gtag. */
  function track(name, props) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      if (props) { for (var k in props) { if (Object.prototype.hasOwnProperty.call(props, k)) payload[k] = props[k]; } }
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the funnel */ }
    try {
      if (GA4_ID && typeof window.gtag === 'function') {
        var ga = { send_to: GA4_ID };
        if (props) { for (var p in props) { if (Object.prototype.hasOwnProperty.call(props, p)) ga[p] = props[p]; } }
        window.gtag('event', name, ga);
      }
    } catch (e) { /* analytics must never break the funnel */ }
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props || {});
    } catch (e) { /* analytics must never break the funnel */ }
  }

  /* Direct-to-Meta failsafe for the funnel's conversion moments (Lead, AddToCart,
     InitiateCheckout). The dataLayer push in track() above is not enough by itself —
     it only reaches Meta if a GTM tag is wired to translate an fs_* dataLayer event into
     the matching Pixel standard event, and nothing guarantees that tag exists. That gap
     is exactly what left the Haut Scan campaign's Lead-optimization measuring zero
     results. Calling fbq() directly here is the same failsafe pattern gtm-head.liquid
     already uses for PageView (GTM does not retroactively fire consent-gated tags) — it
     only sends once fbq is actually initialized (i.e. marketing consent granted), and
     never throws. No PII is ever passed. */
  function metaTrack(eventName, params) {
    try {
      if (typeof window.fbq === 'function') window.fbq('track', eventName, params || {});
    } catch (e) { /* analytics must never break the funnel */ }
  }

  function FaceScanFunnel(root) {
    this.root = root;
    this.config = this.readConfig();
    GA4_ID = (this.config && this.config.ga4Id) || '';
    this.steps = (this.config.leadGate ? ['email'] : []).concat(['live', 'share', 'bridge', 'offer']);
    this.screen = this.steps[0];
    this.engine = null;
    this.realAge = null;
    this.tier = 'm3'; // 3-month Kur is the recommended default
    this.shared = false;
    this.duelRendered = false;
    this.resultTimer = null;
    this.resultReadyAt = null;
    this.shareBlob = null;
    this.shareUrl = '';
    this.previewObjectUrl = '';
    this.readDuelParams();
    document.body.classList.add('face-scan-funnel-shell');
    this.cache();
    this.bind();
    this.go(this.steps[0]);
    this.renderKur();
    this.watchConsent();
    this.initStickyBuy();
    this.initSheet();
    this.initGatePreview();
    this.warmMivolo();
  }

  /* ---------- gate: tap-to-activate live camera preview ----------
     Seeing your OWN face is far more compelling than a stock demo photo, and granting the
     camera is itself a micro-commitment — but the ask must ride a real user gesture. An
     auto-prompt on page load would be reckless here: there is no iOS/Safari gesture handling
     anywhere in the engine, and a denial is unrecoverable for the whole visit (the engine's
     camera-block panel has no retry — only the selfie-upload fallback).

     Deliberately a DUMB <video>: its own getUserMedia, no MediaPipe, no capture, no upload.
     That keeps it out of Art. 9 biometric processing (the consent stays bound to the
     "Start live scan" button) and avoids three landmines of hosting the engine here — the
     engine root sits in a display:none subtree (zero layout box), its constructor force-starts
     the camera, and its own lead modal would pop over the gate while unlocked === false. */
  FaceScanFunnel.prototype.initGatePreview = function () {
    var self = this;
    this.gateDemos = this.root.querySelectorAll('[data-fs-gatedemo]');
    var buttons = this.root.querySelectorAll('[data-fs-gate-preview-start]');
    if (!buttons.length) return;                                   // setting off → no-op
    var supported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    Array.prototype.forEach.call(buttons, function (btn) {
      if (!supported) { btn.hidden = true; return; }
      btn.addEventListener('click', function () { self.startGatePreview(btn); });
    });
  };

  FaceScanFunnel.prototype.setGateDemoState = function (state) {
    Array.prototype.forEach.call(this.gateDemos || [], function (el) {
      el.classList.toggle('is-live', state === 'live');
      el.classList.toggle('is-denied', state === 'denied');
    });
  };

  FaceScanFunnel.prototype.startGatePreview = function (btn) {
    var self = this;
    if (this._gateStream) return;
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    track('fs_gate_preview_start', {});
    // called straight from the click handler → the permission prompt keeps the user gesture
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then(function (stream) {
        self._gateStream = stream;
        var vids = self.root.querySelectorAll('[data-fs-gate-preview]');
        Array.prototype.forEach.call(vids, function (v) {
          v.srcObject = stream;
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        });
        self.setGateDemoState('live');
        track('fs_gate_preview_ready', {});
      })
      .catch(function (err) {
        // denial is survivable HERE (unlike in the engine): the demo photo returns and the
        // form stays fully usable — the visitor can still submit and use the upload path
        self.setGateDemoState('denied');
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        track('fs_gate_preview_denied', { fs_error_type: (err && err.name) || 'unknown' });
      });
  };

  /* Release the preview camera BEFORE the engine asks for it — two live streams on the same
     device can fail on Android. The permission is warm by then, so the engine's getUserMedia
     resolves without a second prompt (and the scan starts faster than it does today). */
  FaceScanFunnel.prototype.stopGatePreview = function () {
    if (this._gateStream) {
      try { this._gateStream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
      this._gateStream = null;
    }
    var vids = this.root.querySelectorAll('[data-fs-gate-preview]');
    Array.prototype.forEach.call(vids, function (v) { v.srcObject = null; });
    this.setGateDemoState('');
  };

  /* Fire-and-forget GPU pre-warm: the cold Modal container needs ~20s to boot, but the
     user spends 20-40s on the email gate + scan first — by result-ready it's warm and
     the age correction lands in ~1-3s instead of silently timing out. */
  FaceScanFunnel.prototype.warmMivolo = function () {
    var url = this.mivoloAgeUrl();
    if (!url) return;
    try { fetch(String(url).replace(/\/$/, '') + '/warm', { method: 'GET', mode: 'cors' }).catch(function () {}); } catch (e) {}
  };

  /* Result bottom sheet (mobile): tap toggles between peek and expanded; drag from the
     grabber follows the finger between the two snap points (flick wins, else nearest).
     Drag starts ONLY from the grabber so the expanded inner scroll never fights it. */
  FaceScanFunnel.prototype.initSheet = function () {
    var self = this;
    this.sheetEl = this.root.querySelector('.fs-console');
    this.sheetGrab = this.root.querySelector('[data-fs-sheet-grab]');
    if (!this.sheetEl || !this.sheetGrab || !this.live) return;
    var startY = 0, startH = 0, moved = false, lastY = 0, lastT = 0, vel = 0;
    /* The peek height is MEASURED from its real content (grabber + verdict + foot), not
       guessed as a viewport fraction — a fixed dvh clipped the CTA over the age on some
       devices/locales. Clamped so it can never eat the face or collapse. */
    var measurePeek = function () {
      if (window.innerWidth > 900 || !self.live.classList.contains('is-result')) return;
      var H = self.live.clientHeight || window.innerHeight;
      var grab = self.sheetGrab.offsetHeight || 0;
      var ageEl = self.root.querySelector('[data-fs-age-block]');
      var footEl = self.root.querySelector('.fs-console__foot');
      var px = grab + (ageEl ? ageEl.offsetHeight : 0) + (footEl ? footEl.offsetHeight : 0) + 16;
      px = Math.max(210, Math.min(H * 0.48, px));
      self._peekPx = px;
      self.live.style.setProperty('--fs-sheet-peek', Math.round(px) + 'px');
    };
    this.measureSheetPeek = measurePeek;
    window.addEventListener('resize', measurePeek);
    var bounds = function () {
      var H = self.live.clientHeight || window.innerHeight;
      var bh = parseFloat(getComputedStyle(self.live).getPropertyValue('--fs-banner-h')) || 0;
      return { peek: self._peekPx || H * 0.34, full: Math.min(H * 0.82, H - bh - 12) };
    };
    var setOpen = function (open) {
      self.live.classList.toggle('is-expanded', !!open);
      self.sheetGrab.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && !self._sheetTracked) { self._sheetTracked = true; track('fs_sheet_expand', {}); }
    };
    this.setSheetOpen = setOpen;
    this.sheetGrab.addEventListener('click', function () {
      if (moved) { moved = false; return; }   // a drag already decided — swallow the ghost click
      setOpen(!self.live.classList.contains('is-expanded'));
    });
    if (!('PointerEvent' in window)) return;  // tap-toggle still works everywhere
    this.sheetGrab.addEventListener('pointerdown', function (e) {
      if (window.innerWidth > 900 || !self.live.classList.contains('is-result')) return;
      startY = lastY = e.clientY; lastT = Date.now(); vel = 0; moved = false;
      startH = self.sheetEl.getBoundingClientRect().height;
      self.sheetEl.classList.add('is-dragging');
      try { self.sheetGrab.setPointerCapture(e.pointerId); } catch (err) {}
    });
    this.sheetGrab.addEventListener('pointermove', function (e) {
      if (!self.sheetEl.classList.contains('is-dragging')) return;
      var dy = startY - e.clientY;
      if (Math.abs(dy) > 6) moved = true;
      var b = bounds();
      self.sheetEl.style.height = Math.max(b.peek, Math.min(b.full, startH + dy)) + 'px';
      var now = Date.now();
      if (now > lastT) { vel = (lastY - e.clientY) / (now - lastT); lastY = e.clientY; lastT = now; }
    });
    var release = function () {
      if (!self.sheetEl.classList.contains('is-dragging')) return;
      var b = bounds();
      var h = self.sheetEl.getBoundingClientRect().height;
      var open = Math.abs(vel) > 0.35 ? vel > 0 : (h - b.peek) > (b.full - b.peek) / 2;
      setOpen(open);
      self.sheetEl.classList.remove('is-dragging');
      self.sheetEl.style.height = '';          // hand back to the CSS snap state
    };
    this.sheetGrab.addEventListener('pointerup', release);
    this.sheetGrab.addEventListener('pointercancel', release);
  };

  /* Mobile-only sticky buy bar on the offer step: appears once the in-card CTA
     scrolls out of view, sits above the cookie-consent banner (--fs-banner-h). */
  FaceScanFunnel.prototype.initStickyBuy = function () {
    var self = this;
    if (!this.stickyBar || !this.addBtn) return;
    if (this.stickyAdd) this.stickyAdd.addEventListener('click', function () { self.addToCart(); });
    if (!('IntersectionObserver' in window)) return;
    this.stickyObserver = new IntersectionObserver(function (entries) {
      var ctaVisible = entries[0] && entries[0].isIntersecting;
      self.stickyBar.hidden = ctaVisible || self.screen !== 'offer' || window.innerWidth > 820;
    }, { threshold: 0 });
    this.stickyObserver.observe(this.addBtn);
  };

  /* Duell arrival state: ?fs_ref=<code> marks a referred visitor, ?fs_duel=<result-id>
     carries the challenger's shared result for the age comparison. Both are persisted
     in sessionStorage so reloads mid-funnel keep the Duell context. IDs are validated
     before ever touching a URL again. */
  FaceScanFunnel.prototype.readDuelParams = function () {
    var ref = '', duel = '';
    try {
      var p = new URL(window.location.href).searchParams;
      ref = p.get('fs_ref') || '';
      duel = p.get('fs_duel') || '';
    } catch (e) {}
    try {
      if (ref) window.sessionStorage.setItem('fs_ref', ref);
      else ref = window.sessionStorage.getItem('fs_ref') || '';
      if (duel && /^[a-z0-9_-]{6,64}$/i.test(duel)) window.sessionStorage.setItem('fs_duel', duel);
      duel = window.sessionStorage.getItem('fs_duel') || '';
    } catch (e) {}
    this.referred = !!ref;
    this.duelId = (/^[a-z0-9_-]{6,64}$/i.test(duel)) ? duel : '';
  };

  /* The Duell pricing only ever activates when a real code is configured (honesty
     guard) AND the visitor earned it — by sharing, or by arriving via a shared link. */
  FaceScanFunnel.prototype.duellActive = function () {
    return !!((this.config.duellCode || '').trim()) && (this.shared || this.referred || !!this.duelId);
  };

  /* Reserve space for the Shopify cookie-consent banner (a fixed bottom overlay) so it
     never permanently covers the KPI cards or the proceed CTA. Sets --fs-banner-h to the
     banner's live height on the funnel root; 0 when it's absent or dismissed. */
  FaceScanFunnel.prototype.watchConsent = function () {
    var root = this.root, observed = null, ro = null, scheduled = false;
    function update() {
      var b = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      var h = 0;
      if (b) {
        var cs = getComputedStyle(b), rect = b.getBoundingClientRect();
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && rect.height > 0 && rect.bottom > window.innerHeight - 8) {
          h = Math.ceil(rect.height);
        }
        if (b !== observed && window.ResizeObserver) {
          if (ro) ro.disconnect();
          ro = new ResizeObserver(schedule);
          ro.observe(b);
          observed = b;
        }
      }
      root.style.setProperty('--fs-banner-h', h + 'px');
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () { scheduled = false; update(); });
    }
    update();
    try { new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    window.addEventListener('resize', schedule);
  };

  FaceScanFunnel.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-fs-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  };

  FaceScanFunnel.prototype.cache = function () {
    var r = this.root;
    this.engineRoot = r.querySelector('[data-face-scan]');
    this.gateForm = r.querySelector('[data-fs-gate-form]');
    this.gateEmail = r.querySelector('[data-fs-gate-email]');
    this.gateNewsletter = r.querySelector('[data-fs-gate-newsletter]');
    this.gateSubmit = r.querySelector('[data-fs-gate-submit]');
    this.live = r.querySelector('[data-fs-live]');
    this.toShare = r.querySelector('[data-fs-to-share]');
    this.ageNode = r.querySelector('[data-face-scan-age]');
    this.ageGap = r.querySelector('[data-fs-age-gap]');
    this.ageCompare = r.querySelector('[data-fs-age-compare]');
    this.ageOptin = r.querySelector('[data-fs-age-optin]');
    this.agePanel = r.querySelector('[data-fs-age-panel]');
    this.ageInput = r.querySelector('[data-fs-age-input]');
    this.ageText = r.querySelector('[data-fs-age-text]');
    this.trajectory = r.querySelector('[data-fs-trajectory]');
    this.trajectorySvg = r.querySelector('[data-fs-trajectory-svg]');
    this.snapCanvas = r.querySelector('[data-fs-snap-canvas]');
    this.snapAge = r.querySelector('[data-fs-snap-age]');
    this.snapGap = r.querySelector('[data-fs-snap-gap]');
    this.shareBtn = r.querySelector('[data-fs-share]');
    this.shareNote = r.querySelector('[data-fs-share-note]');
    this.bridge = r.querySelector('[data-fs-step="bridge"]');
    this.addBtn = r.querySelector('[data-fs-add]');
    this.cartLabel = r.querySelector('[data-fs-cart-label]');
    // product media gallery
    this.gallerySlides = Array.prototype.slice.call(r.querySelectorAll('[data-fs-gallery-slide]'));
    this.galleryThumbs = Array.prototype.slice.call(r.querySelectorAll('[data-fs-thumb]'));
    // Kur buy-box
    this.kurTabs = Array.prototype.slice.call(r.querySelectorAll('[data-fs-kur]'));
    this.kurTotal = r.querySelector('[data-fs-kur-total]');
    this.kurAnchor = r.querySelector('[data-fs-kur-anchor]');
    this.kurPill = r.querySelector('[data-fs-kur-pill]');
    this.kurDoses = r.querySelector('[data-fs-kur-doses]');
    this.kurBilling = r.querySelector('[data-fs-kur-billing]');
    this.ctaPrice = r.querySelector('[data-fs-cta-price]');
    this.duellChip = r.querySelector('[data-fs-duell-chip]');
    this.duellChipText = r.querySelector('[data-fs-duell-chip-text]');
    // Duell comparison card (live console)
    this.duelCard = r.querySelector('[data-fs-duel-card]');
    this.duelYou = r.querySelector('[data-fs-duel-you]');
    this.duelThem = r.querySelector('[data-fs-duel-them]');
    this.duelYouLabel = r.querySelector('[data-fs-duel-you-label]');
    this.duelThemLabel = r.querySelector('[data-fs-duel-them-label]');
    this.duelVerdict = r.querySelector('[data-fs-duel-verdict]');
    // share modal
    this.shareModal = r.querySelector('[data-fs-share-modal]');
    this.modalNote = r.querySelector('[data-fs-modal-note]');
    this.sharePreview = r.querySelector('[data-fs-share-preview]');
    // sticky mobile buy bar (offer step)
    this.stickyBar = r.querySelector('[data-fs-stickybuy]');
    this.stickyLabel = r.querySelector('[data-fs-sticky-label]');
    this.stickyAdd = r.querySelector('[data-fs-sticky-add]');
    this.shareUrlEl = r.querySelector('[data-fs-share-url]');
    this.shareCopyBtn = r.querySelector('[data-fs-share-copy]');
    this.shareWhatsapp = r.querySelector('[data-fs-share-whatsapp]');
    this.shareEmail = r.querySelector('[data-fs-share-email]');
    this.shareX = r.querySelector('[data-fs-share-x]');
    this.shareDownload = r.querySelector('[data-fs-share-download]');
  };

  FaceScanFunnel.prototype.bind = function () {
    var self = this;
    if (this.gateEmail) {
      this.gateEmail.addEventListener('input', function () {
        if (self.gateSubmit) self.gateSubmit.disabled = !EMAIL_RE.test(self.gateEmail.value.trim());
      });
    }
    if (this.gateForm) this.gateForm.addEventListener('submit', function (e) { e.preventDefault(); self.submitGate(); });
    if (this.toShare) this.toShare.addEventListener('click', function () { self.go('share'); });
    // no-camera / denied fallback: "upload a selfie"
    var upBtn = this.root.querySelector('[data-fs-upload]');
    var upInput = this.root.querySelector('[data-fs-upload-input]');
    if (upBtn && upInput) {
      upBtn.addEventListener('click', function () { upInput.click(); });
      upInput.addEventListener('change', function () { if (upInput.files && upInput.files[0]) self.applyUploadedSelfie(upInput.files[0]); });
    }
    var toBridge = this.root.querySelector('[data-fs-to-bridge]');
    if (toBridge) toBridge.addEventListener('click', function () { self.go('bridge'); });
    var toOffer = this.root.querySelector('[data-fs-to-offer]');
    if (toOffer) toOffer.addEventListener('click', function () { self.go('offer'); });
    if (this.shareBtn) this.shareBtn.addEventListener('click', function () { self.share(); });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fs-restart]'), function (b) {
      b.addEventListener('click', function () { self.restart(); });
    });
    if (this.ageOptin) this.ageOptin.addEventListener('click', function () { self.optInAge(); });
    if (this.ageInput) this.ageInput.addEventListener('input', function () {
      var v = parseInt(self.ageInput.value, 10);
      if (v >= 16 && v <= 99) self.setAge(v);
    });

    this.kurTabs.forEach(function (b) {
      b.addEventListener('click', function () { self.selectTier(b.getAttribute('data-fs-kur'), true); });
    });
    // gallery: thumbnail clicks + touch swipe on the main image
    this.galleryThumbs.forEach(function (b) {
      b.addEventListener('click', function () { self.setGallery(parseInt(b.getAttribute('data-fs-thumb'), 10)); });
    });
    var galleryMain = this.root.querySelector('.fs-gallery__main');
    if (galleryMain && this.gallerySlides.length > 1) {
      var touchX = null;
      galleryMain.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
      galleryMain.addEventListener('touchend', function (e) {
        if (touchX == null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) < 40) return;
        self.setGallery((self.galleryIndex || 0) + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }
    if (this.addBtn) this.addBtn.addEventListener('click', function () { self.addToCart(); });

    // share modal
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fs-share-close]'), function (b) {
      b.addEventListener('click', function () { self.closeShareModal(); });
    });
    if (this.shareCopyBtn) this.shareCopyBtn.addEventListener('click', function () { self.copyShareLink(); });
    if (this.shareDownload) this.shareDownload.addEventListener('click', function () {
      if (self.shareBlob) self.downloadBlob(self.shareBlob, 'zellaforte-hautalter.jpg');
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.closeShareModal(); });
  };

  /* ---------- step machine ---------- */
  FaceScanFunnel.prototype.go = function (name) {
    if (this.steps.indexOf(name) === -1) name = this.steps[0];
    this.screen = name;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fs-step]'), function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-fs-step') === name);
    });
    this.root.classList.toggle('is-immersive', name === 'live');
    this.updateRail();
    window.scrollTo(0, 0);
    if (this.stickyBar) {
      this.stickyBar.hidden = true;
      // re-arm the observer so the bar re-evaluates on the freshly-scrolled offer step
      if (name === 'offer' && this.stickyObserver && this.addBtn) {
        this.stickyObserver.unobserve(this.addBtn);
        this.stickyObserver.observe(this.addBtn);
      }
    }
    /* steps swap in place — without this, mobile keeps the previous scroll depth
       and the freshly shown step looks like "nothing happened" */
    window.scrollTo(0, 0);
    track('fs_step_view', { fs_step: name });
    if (name === 'live') this.enterLive();
    if (name === 'share') this.enterShare();
    if (name === 'bridge') this.enterBridge();
    if (name === 'offer') this.enterOffer();
  };

  FaceScanFunnel.prototype.next = function () {
    var i = this.steps.indexOf(this.screen);
    this.go(this.steps[Math.min(this.steps.length - 1, i + 1)]);
  };

  FaceScanFunnel.prototype.updateRail = function () {
    var idx = this.steps.indexOf(this.screen);
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fs-rail-step]'), function (el) {
      var pos = self.steps.indexOf(el.getAttribute('data-fs-rail-step'));
      el.classList.toggle('is-current', pos === idx);
      el.classList.toggle('is-done', pos > -1 && pos < idx);
      el.style.display = pos === -1 ? 'none' : '';
    });
  };

  FaceScanFunnel.prototype.restart = function () {
    /* re-arm the engine's result lock — without this, "start new scan" would show
       the previous frozen age forever (the engine keeps running across restarts) */
    if (this.engine && typeof this.engine.unlockResult === 'function') this.engine.unlockResult();
    // kill the readiness poll — a leaked interval re-fires onResultReady on the email step
    // (capturing + uploading a frame outside the scan) and locks the next scan instantly
    if (this.resultTimer) { window.clearInterval(this.resultTimer); this.resultTimer = null; }
    if (this._settleTimer) { window.clearInterval(this._settleTimer); this._settleTimer = null; }
    this.resultReadyAt = null;
    this.realAge = null;
    this._outcomePushed = false;
    this._duelThem = null;
    // drop the cloud age so the next scan re-captures — never inherit the prior face's age.
    // Bump the scan generation so any in-flight MiVOLO fetch resolves into a no-op, abort it,
    // and stop a running age-morph — otherwise the old face's age could lock the new scan.
    this._scanGen = (this._scanGen || 0) + 1;
    this._mivoloAge = null;
    this._mivoloBusy = false;
    this._uploadedImage = null;   // no-camera fallback: drop the uploaded selfie
    this._heroFrozen = false;     // next scan freezes its own hero frame
    this._heroLandmarks = null;
    this.revealNumber();   // drop any analysing loader + its fallback timer
    this._ageRevealed = false;   // AFTER revealNumber (which sets it true) — the new scan has no number yet
    this.stopGatePreview();      // a rescan may land back on the gate — never leave a stream open
    if (this._ageAnim) { window.clearInterval(this._ageAnim); this._ageAnim = null; }
    if (this._mivoloCtrl) { try { this._mivoloCtrl.abort(); } catch (e) {} this._mivoloCtrl = null; }
    this.setRefine('');
    // collapse the result sheet + restart the camera if the bridge step released it
    if (this.setSheetOpen) this.setSheetOpen(false);
    if (this.sheetEl) { this.sheetEl.classList.remove('is-dragging'); this.sheetEl.style.height = ''; }
    this._peekPx = null;
    if (this.live) this.live.style.removeProperty('--fs-sheet-peek');
    if (this.engine && this.engine.stream === null && typeof this.engine.startCamera === 'function') {
      try { this.engine.startCamera(); } catch (e) {}
    }
    if (this.ageCompare) this.ageCompare.hidden = true;
    if (this.agePanel) this.agePanel.hidden = true;
    if (this.ageInput) this.ageInput.value = '';
    if (this.ageOptin) this.ageOptin.hidden = false;
    if (this.ageGap) this.ageGap.hidden = true;
    if (this.snapGap) this.snapGap.hidden = true;
    if (this.live) this.live.classList.remove('is-result');
    if (this.toShare) this.toShare.disabled = true;
    this.duelRendered = false;
    if (this.duelCard) this.duelCard.hidden = true;
    // un-stick the gate: submitGate() disabled the button; a returning email is still valid
    if (this.gateSubmit && this.gateEmail) this.gateSubmit.disabled = !EMAIL_RE.test(this.gateEmail.value.trim());
    this.closeShareModal();
    // rescan should not re-ask for the email once the lead is captured — go straight to the scan
    this.go(this._leadDone ? 'live' : this.steps[0]);
  };

  /* ---------- engine bridge ---------- */
  FaceScanFunnel.prototype.getEngine = function () {
    if (this.engine) return this.engine;
    if (this.engineRoot && typeof window.initFaceScan === 'function') this.engine = window.initFaceScan(this.engineRoot);
    else if (this.engineRoot) this.engine = this.engineRoot.__faceScanLanding || null;
    return this.engine;
  };

  /* ---------- step 1: email gate ---------- */
  FaceScanFunnel.prototype.submitGate = function () {
    var self = this;
    var email = (this.gateEmail && this.gateEmail.value || '').trim();
    if (!EMAIL_RE.test(email)) return;
    var newsletter = !!(this.gateNewsletter && this.gateNewsletter.checked);
    var firstName = this.deriveName(email);
    if (this.gateSubmit) this.gateSubmit.disabled = true;

    track('fs_lead', { fs_newsletter_optin: newsletter });
    metaTrack('Lead', { content_name: 'Haut Scan', content_category: 'face_scan_funnel' });
    // hand the camera over: release the gate preview synchronously so the engine (next line)
    // can acquire the device cleanly — permission is already warm, so no second prompt
    this.stopGatePreview();
    var eng = this.getEngine();
    /* Go to the camera step IMMEDIATELY — the OS permission prompt then appears over
       the camera UI (where it makes sense) instead of the email form, and the Klaviyo
       subscribe round-trip finishes in the background (its result gates nothing). */
    if (eng) { try { eng.markUnlocked(); eng.setLockedState(false); } catch (e) {} }
    this._leadDone = true;   // lead captured → a later rescan skips the email gate
    this.go('live');
    if (!eng) return;
    try { eng.trackLead(firstName, email, newsletter); } catch (e) {}
    /* List subscription sets marketing consent in Klaviyo — UWG-legal only
       when the newsletter box was actually ticked. Non-consenting leads are
       still recorded as profiles via trackLead above (no $consent). */
    if (newsletter) {
      try {
        var sub = eng.subscribeLead(firstName, email, newsletter);
        if (sub && typeof sub.catch === 'function') sub.catch(function () {});
      } catch (e) {}
    }
  };

  FaceScanFunnel.prototype.deriveName = function (email) {
    var local = String(email).split('@')[0].replace(/[._-]+/g, ' ').trim();
    if (!local) return '';
    return local.charAt(0).toUpperCase() + local.slice(1);
  };

  /* ---------- step 2: live scan ---------- */
  FaceScanFunnel.prototype.enterLive = function () {
    var self = this;
    this.getEngine();
    if (this.resultTimer) return;
    this.resultTimer = window.setInterval(function () {
      if (!self.cameraErrorTracked && self.engineRoot) {
        var blocked = self.engineRoot.querySelector('[data-face-scan-camera-block]');
        if (blocked && !blocked.hidden) {
          self.cameraErrorTracked = true;
          track('fs_error', { fs_error_type: 'camera_denied' });
        }
      }
      var ageTxt = self.ageNode ? self.ageNode.textContent.trim() : '';
      var ready = (self.engine && self.engine.hasVisibleResult) || (ageTxt && ageTxt !== '--');
      if (ready && !self.resultReadyAt) { self.resultReadyAt = Date.now(); self.onResultReady(); }
      /* the snapshot CTA unlocks only once there is a REAL number to capture: the engine
         locked the result (cloud age landed / converged) OR the number was revealed on a
         cloud failure. Never unlock over a "--" while a cold boot is still analysing. 45s
         safety valve (>= the reveal fallback so it never unlocks over a "--") so a scene
         that never resolves still lets the user continue. */
      if (self.resultReadyAt && ((self.engine && self.engine.resultLocked) || self._ageRevealed || Date.now() - self.resultReadyAt > 50000)) {
        if (self.toShare) self.toShare.disabled = false;
        if (!self._heroFrozen) self.freezeHeroFrame();   // capture the shareable frame at the locked moment
        // status pill flips to "✓ Analyse fertig" — the engine stops writing it once
        // locked, and the stale "NO FACE"/"hold still" quality pill is CSS-hidden on result
        var statusEl = self.root.querySelector('[data-face-scan-app-status]');
        var i18n = self.config.i18n || {};
        if (statusEl && i18n.resultPill) statusEl.textContent = i18n.resultPill;
        window.clearInterval(self.resultTimer);
        self.resultTimer = null;
      }
    }, 400);
  };

  FaceScanFunnel.prototype.onResultReady = function () {
    if (this.live) this.live.classList.add('is-result');
    // size the peek to its real content (once revealed, and again after the rise settles)
    if (this.measureSheetPeek) {
      window.setTimeout(this.measureSheetPeek, 80);
      window.setTimeout(this.measureSheetPeek, 650);
    }
    // Hold the big number behind an "analysing" loader until the ACCURATE cloud age lands —
    // never flash the over-aged on-device estimate then morph it down. On a cold GPU boot the
    // correct age can take ~20-40s; we WAIT for it rather than show a wrong number. After ~9s
    // we swap in a reassurance line so a longer first-scan wait reads as intentional. Hard
    // safety at 42s (>= the fetch budget) so the result can never truly hang.
    // (No-op unless mivoloAge is on.)
    if (this.mivoloAgeUrl() && this._mivoloAge == null) {
      if (this.live) this.live.classList.add('is-analyzing');
      this.setRefine('analyzing');
      var selfR = this;
      this._warmTimer = window.setTimeout(function () {
        if (selfR.live && selfR.live.classList.contains('is-analyzing')) selfR.setRefine('warming');
      }, 9000);
      // >= the absolute worst-case fetch time (40s primary + 6s retry) so the fallback can
      // never pre-empt a still-in-flight cloud result and trigger a morph-down.
      this._analyzeTimer = window.setTimeout(function () { selfR.revealNumber(); selfR.setRefine(''); }, 48000);
    }
    this.applyMivoloAge();
    if (this.config.ageOptional && this.ageCompare) this.ageCompare.hidden = false;
    if (this.config.showTrajectory) this.renderTrajectory();
    track('fs_result_ready', { fs_age_band: this.ageBand() });
    var self = this;
    this.tagWeakest();
    window.setTimeout(function () { self.tagWeakest(); }, 1500);
    /* Outcome + duel wait until the age source SETTLES (MiVOLO landed or gave up, 12s cap)
       so Klaviyo/GA segment on the number the user actually saw, and the duel verdict is
       computed from the final age instead of a value that corrects seconds later. */
    this.schedulePushOutcome();
  };

  FaceScanFunnel.prototype.schedulePushOutcome = function () {
    var self = this, started = Date.now();
    if (this._settleTimer) window.clearInterval(this._settleTimer);
    this._settleTimer = window.setInterval(function () {
      var settled = self._mivoloAge != null || !self._mivoloBusy;
      if (!settled && Date.now() - started < 12000) return;
      window.clearInterval(self._settleTimer);
      self._settleTimer = null;
      self.tagWeakest();
      self.pushOutcome();
      self.maybeRenderDuel();
    }, 500);
  };

  /* Duell comparison: fetch the challenger's shared result and compare skin ages.
     Every failure (expired 7-day TTL, 404, network, malformed metrics, missing own
     age) skips silently — the card only appears when both ages are real. */
  FaceScanFunnel.prototype.maybeRenderDuel = function () {
    var self = this;
    if (this.duelRendered || !this.duelId || !this.duelCard) return;
    this.duelRendered = true;
    var base = String(this.config.resultApiBaseUrl || '/face-scan/results').replace(/\/$/, '');
    var url = base + '/' + encodeURIComponent(this.duelId);
    var timeout = new Promise(function (resolve, reject) { window.setTimeout(function () { reject(new Error('duel timeout')); }, 6000); });
    Promise.race([fetch(url), timeout])
      .then(function (res) { if (!res || !res.ok) throw new Error('duel unavailable'); return res.json(); })
      .then(function (data) {
        var m = (data && data.metrics) || {};
        var them = parseInt(m.visual_age || m.age, 10);
        var mine = self._mivoloAge != null ? self._mivoloAge : self.visualAge();   // settled value, not mid-morph DOM
        if (!isFinite(them) || them < 10 || them > 100 || mine == null) return;
        var i18n = self.config.i18n || {};
        if (self.duelYouLabel) self.duelYouLabel.textContent = i18n.duellYou || '';
        if (self.duelThemLabel) self.duelThemLabel.textContent = i18n.duellThem || '';
        if (self.duelYou) self.duelYou.textContent = String(mine);
        if (self.duelThem) self.duelThem.textContent = String(them);
        self._duelThem = them;   // cached so a late age correction can recompute the verdict
        var verdict = mine < them ? i18n.duellWin : (mine > them ? i18n.duellLose : i18n.duellTie);
        if (self.duelVerdict) self.duelVerdict.textContent = verdict || '';
        self.duelCard.hidden = false;
        track('fs_duel_view', { fs_duel_outcome: mine < them ? 'win' : (mine > them ? 'lose' : 'tie') });
      })
      .catch(function () { /* expired/unreachable challenger result — no card */ });
  };

  // Recompute the visible duel numbers/verdict from the CURRENT age (a MiVOLO correction
  // landing after the card rendered would otherwise contradict the hero number).
  FaceScanFunnel.prototype.refreshDuel = function () {
    if (this._duelThem == null || !this.duelCard || this.duelCard.hidden) return;
    var mine = this._mivoloAge != null ? this._mivoloAge : this.visualAge();
    if (mine == null) return;
    var i18n = this.config.i18n || {};
    var them = this._duelThem;
    if (this.duelYou) this.duelYou.textContent = String(mine);
    var verdict = mine < them ? i18n.duellWin : (mine > them ? i18n.duellLose : i18n.duellTie);
    if (this.duelVerdict) this.duelVerdict.textContent = verdict || '';
  };

  /* Coarse age band (never the exact number) for segmentation. Prefers the settled
     MiVOLO value over the DOM — the hero node lags behind during the morph animation. */
  FaceScanFunnel.prototype.ageBand = function () {
    var a = this._mivoloAge != null ? this._mivoloAge : this.visualAge();
    if (!a) return '';
    var lo = Math.floor(a / 10) * 10;
    return lo + '-' + (lo + 9);
  };

  /* Attach coarse scan outcome to the (already identified) Klaviyo profile + dataLayer,
     enabling outcome-keyed flows. Covered by the gate consent copy. */
  FaceScanFunnel.prototype.pushOutcome = function () {
    var band = this.ageBand();
    var weak = this.weakestKey || '';
    if (!band && !weak) return;
    // GA event once per scan — UNLESS a late correction changed the band, then supersede
    // (Klaviyo identify always re-runs so the profile ends on the number the user saw).
    if (!this._outcomePushed || band !== this._outcomeBand) {
      var props = { fs_age_band: band };
      if (weak) props.fs_weakest_lever = weak;
      track('fs_scan_outcome', props);
      this._outcomePushed = true;
      this._outcomeBand = band;
    }
    try {
      window._learnq = window._learnq || [];
      var idf = { face_scan_age_band: band };
      if (weak) idf.face_scan_weakest_lever = weak;
      // buy-loop anchors: enable the 30-day rescan reminder (the natural KeraBiotic
      // moment, honoring the Day-0 hook) + scanned-but-not-bought nurture flows
      idf.face_scan_completed = true;
      idf.face_scan_last_scan_at = new Date().toISOString();
      window._learnq.push(['identify', idf]);
    } catch (e) {}
  };

  /* Flag the single weakest actionable lever (glow/tone/texture — NOT the composite
     Haut-Score) so the result points to one honest "biggest lever". */
  FaceScanFunnel.prototype.tagWeakest = function () {
    var r = this.root;
    var keys = ['glow', 'tone', 'texture'];
    var lowestCard = null, lowestVal = Infinity, values = [];
    for (var i = 0; i < keys.length; i++) {
      var valEl = r.querySelector('[data-face-scan-' + keys[i] + ']');
      var card = r.querySelector('[data-fs-score-card="' + keys[i] + '"]');
      if (!valEl || !card) return;
      var n = parseInt(valEl.textContent, 10);
      if (isNaN(n)) return; // wait until all three have settled to avoid mistagging
      values.push(n);
      if (n < lowestVal) { lowestVal = n; lowestCard = card; }
    }
    Array.prototype.forEach.call(r.querySelectorAll('.fs-score.is-weak'), function (c) { c.classList.remove('is-weak'); });
    /* honesty guard: only tag a "biggest lever" when it clearly IS one — with a
       near-tie (<3pts to the runner-up) the pick would be arbitrary noise */
    values.sort(function (a, b) { return a - b; });
    if (values[1] - values[0] < 3) { this.weakestKey = ''; return; }
    if (lowestCard) {
      lowestCard.classList.add('is-weak');
      this.weakestKey = lowestCard.getAttribute('data-fs-score-card') || '';
    }
  };

  FaceScanFunnel.prototype.visualAge = function () {
    var n = parseInt(this.ageNode ? this.ageNode.textContent : '', 10);
    return isNaN(n) ? null : n;
  };

  FaceScanFunnel.prototype.optInAge = function () {
    // reveal the typed age field — the gap appears once a real age is entered
    if (this.ageOptin) this.ageOptin.hidden = true;
    if (this.agePanel) this.agePanel.hidden = false;
    if (this.ageInput) { try { this.ageInput.focus(); } catch (e) {} }
  };

  FaceScanFunnel.prototype.setAge = function (val) {
    val = Math.max(16, Math.min(99, val || 30));
    this.realAge = val;
    var va = this.visualAge();
    if (va == null) return;
    var gap = va - val, older = gap > 0;
    if (this.ageText) {
      var key = older ? 'older' : (gap === 0 ? 'same' : 'younger');
      var tpl = this.ageText.getAttribute('data-' + key) || '';
      this.ageText.innerHTML = tpl.replace('{gap}', Math.abs(gap));
    }
    // brag-friendly pill: "7 years younger" (green) / "5 years older" / "right on"
    var i18n = this.config.i18n || {};
    var gapLabel = older ? (i18n.ageGapOlder || '+{gap}').replace('{gap}', gap)
      : (gap === 0 ? (i18n.ageGapSame || '±0') : (i18n.ageGapYounger || '−{gap}').replace('{gap}', Math.abs(gap)));
    [this.ageGap, this.snapGap].forEach(function (el) {
      if (!el) return;
      el.hidden = false;
      el.textContent = gapLabel;
      if (older) el.removeAttribute('data-younger'); else el.setAttribute('data-younger', '');
    });
  };

  FaceScanFunnel.prototype.renderTrajectory = function () {
    if (!this.trajectory || !this.trajectorySvg) return;
    var base = this.visualAge() || 38;
    var W = 400, H = 120, pad = 10;
    var min = base - 2, max = base + 7;
    var x = function (i) { return pad + (i / 5) * (W - pad * 2); };
    var y = function (v) { return H - pad - ((v - min) / (max - min)) * (H - pad * 2); };
    var without = [], wth = [];
    for (var i = 0; i <= 5; i++) { without.push(base + i * 1.05); wth.push(base + i * 0.28); }
    var line = function (a) { return a.map(function (v, i) { return (i ? 'L' : 'M') + x(i).toFixed(0) + ' ' + y(v).toFixed(0); }).join(' '); };
    var lblToday = this.trajectory.getAttribute('data-today') || '';
    var lblWithout = this.trajectory.getAttribute('data-without') || '';
    var lblWith = this.trajectory.getAttribute('data-with') || '';
    this.trajectory.hidden = false;
    this.trajectorySvg.innerHTML =
      '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" height="' + H + '" font-family="var(--fs-font)">' +
      '<line x1="' + pad + '" y1="' + (H - pad) + '" x2="' + (W - pad) + '" y2="' + (H - pad) + '" stroke="rgba(255,247,237,.2)"/>' +
      '<path d="' + line(without) + '" stroke="var(--orange-300)" stroke-width="2" fill="none" stroke-dasharray="3 4"/>' +
      '<path d="' + line(wth) + '" stroke="var(--orange)" stroke-width="2.5" fill="none"/>' +
      '<circle cx="' + x(5).toFixed(0) + '" cy="' + y(without[5]).toFixed(0) + '" r="3.6" fill="var(--orange-300)"/>' +
      '<circle cx="' + x(5).toFixed(0) + '" cy="' + y(wth[5]).toFixed(0) + '" r="3.6" fill="var(--orange)"/>' +
      '<text x="' + x(0).toFixed(0) + '" y="' + (y(base) - 8).toFixed(0) + '" fill="rgba(255,247,237,.7)" font-size="10" font-weight="600">' + lblToday + '</text>' +
      '<text x="' + x(5).toFixed(0) + '" y="' + (y(without[5]) + 14).toFixed(0) + '" text-anchor="end" fill="var(--orange-300)" font-size="10" font-weight="600">' + lblWithout + '</text>' +
      '<text x="' + x(5).toFixed(0) + '" y="' + (y(wth[5]) - 9).toFixed(0) + '" text-anchor="end" fill="var(--orange)" font-size="10" font-weight="600">' + lblWith + '</text>' +
      '</svg>';
  };

  /* ---------- MiVOLO v2 authoritative age (opt-in) ----------
     OFF unless config.mivoloAge is true. The on-device model shows an INSTANT estimate;
     then — once, at live result-ready (onResultReady) — we send ONE frame to the Cloudflare
     Worker (/face-scan/age → MiVOLO on Modal) and, when it answers, animate the single age
     source (this.ageNode) from the estimate to the accurate number. That node is what the
     snap card, share image and Klaviyo all read; the snapshot step reuses the cached result.
     Any failure/timeout silently keeps the on-device estimate (no dead-end). The Worker
     returns 503 until endpoint+key are set, so this no-ops safely even if toggled early.
     ⚠️ Uploading the selfie is DSGVO Art. 9 processing — do NOT enable mivoloAge until a
     biometric-consent gate (shown before capture) + a signed DPA + EU region are live. */
  FaceScanFunnel.prototype.mivoloAgeUrl = function () {
    if (!this.config || !this.config.mivoloAge) return '';
    // explicit standalone worker URL (workers.dev, cross-origin — CORS handles it); else
    // derive from the results base for the same-origin route variant.
    if (this.config.mivoloAgeEndpoint) return String(this.config.mivoloAgeEndpoint);
    var base = String(this.config.resultApiBaseUrl || '/face-scan/results');
    return base.replace(/\/results\/?$/, '/age');
  };

  FaceScanFunnel.prototype.captureAgeBlob = function () {
    var self = this;
    return new Promise(function (resolve) {
      // FIX A — prefer the engine's last quality-passing frame (a real frame of the
      // SCANNED face) over the live <video>. By the moment this fires the live element
      // may show a different face/moment (the capture race that produced wrong ages).
      var stash = (self.engine && typeof self.engine.getLastGoodFrame === 'function') ? self.engine.getLastGoodFrame() : null;
      if (stash && stash.canvas && stash.canvas.toBlob && (Date.now() - stash.at) < 3000) {
        stash.canvas.toBlob(function (b) { resolve(b); }, 'image/jpeg', 0.85);
        return;
      }
      // Fallback: no fresh stash (e.g. age model unavailable) — grab the live frame.
      var video = self.root.querySelector('[data-face-scan-video]');
      if (!video || !video.videoWidth || !video.videoHeight) { resolve(null); return; }
      var longSide = Math.max(video.videoWidth, video.videoHeight);
      var scale = longSide > 720 ? 720 / longSide : 1;
      var w = Math.round(video.videoWidth * scale), h = Math.round(video.videoHeight * scale);
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      // TRUE (un-mirrored) frame — the preview is mirrored; MiVOLO wants real orientation.
      cv.getContext('2d').drawImage(video, 0, 0, w, h);
      if (cv.toBlob) cv.toBlob(function (b) { resolve(b); }, 'image/jpeg', 0.85);
      else resolve(null);
    });
  };

  /* Structured result: {age} | {error, retryable}. Budget is deliberately GENEROUS (40s):
     a cold GPU boot can need ~20-40s, and it is far better to wait behind the analysing
     loader for the CORRECT age than to time out and reveal the over-aged on-device number.
     A hard failure (billing/down) returns an error fast (not a timeout) → we reveal the
     on-device estimate immediately; only genuine boot-latency actually consumes the budget.
     One short 6s retry covers a transient network blip. */
  FaceScanFunnel.prototype.fetchMivoloAge = function (blob) {
    var self = this;
    var url = this.mivoloAgeUrl();
    if (!url || !blob) return Promise.resolve({ error: 'unavailable' });
    var attempt = function (budgetMs) {
      var fd = new FormData(); fd.append('image', blob, 'selfie.jpg');
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      self._mivoloCtrl = ctrl;   // so restart() can abort an in-flight request
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, budgetMs) : null;
      return fetch(url, { method: 'POST', body: fd, signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) {
          if (timer) clearTimeout(timer);
          self._mivoloCtrl = null;
          return r.json().catch(function () { return {}; }).then(function (d) {
            if (!r.ok) return { error: 'http_' + r.status, retryable: r.status >= 500 };
            if (typeof d.age === 'number' && isFinite(d.age)) {
              return { age: Math.max(13, Math.min(95, Math.round(d.age))) };
            }
            return { error: d && d.error === 'no_face' ? 'no_face' : 'bad_response' };
          });
        })
        .catch(function (e) {
          if (timer) clearTimeout(timer);
          self._mivoloCtrl = null;
          var aborted = e && e.name === 'AbortError';
          return { error: aborted ? 'timeout' : 'network', retryable: !aborted };
        });
    };
    return attempt(40000).then(function (res) {
      return (res.error && res.retryable) ? attempt(6000) : res;
    });
  };

  // Count the hero age node from its current estimate to the corrected value (feels like a
  // "refine", not a jarring swap). ~45ms/step, capped at 16 steps.
  FaceScanFunnel.prototype.animateAge = function (to, done) {
    var self = this, node = this.ageNode;
    to = Math.round(to);
    var from = node ? parseInt(node.textContent, 10) : NaN;
    if (!node || !isFinite(from) || from === to) { if (node) node.textContent = String(to); if (done) done(); return; }
    var steps = Math.min(16, Math.abs(to - from)), i = 0;
    if (self._ageAnim) window.clearInterval(self._ageAnim);
    self._ageAnim = window.setInterval(function () {
      i += 1;
      node.textContent = String(Math.round(from + (to - from) * (i / steps)));
      if (i >= steps) { window.clearInterval(self._ageAnim); self._ageAnim = null; node.textContent = String(to); if (done) done(); }
    }, 45);
  };

  // Small chip next to the age: "Verfeinere …" while confirming, "Bestätigt" once corrected.
  FaceScanFunnel.prototype.setRefine = function (state) {
    if (this.refineChip === undefined) this.refineChip = this.root.querySelector('[data-fs-age-refine]');
    var el = this.refineChip;
    if (!el) return;
    if (!state) { el.hidden = true; el.removeAttribute('data-state'); return; }
    var i18n = this.config.i18n || {};
    // tolerate a missing locale key (Shopify renders "Translation missing: …") → German fallback
    var pick = function (v, fb) { return (v && !/translation missing/i.test(v)) ? v : fb; };
    el.textContent = state === 'confirmed' ? pick(i18n.ageConfirmed, 'Präzisiert')
      : state === 'warming' ? pick(i18n.ageWarming, 'Analyse-Engine startet – einen Moment …')
      : state === 'analyzing' ? pick(i18n.ageAnalyzing, 'Präzisiere dein Ergebnis …')
      : pick(i18n.ageRefining, 'Wird präzisiert …');
    // 'warming' shares the analysing pulse styling
    el.setAttribute('data-state', state === 'warming' ? 'analyzing' : state);
    el.hidden = false;
  };

  // Re-derive every consumer of the age from the NEW number — the gap pill, the trajectory
  // baseline and the duel verdict must never contradict the hero.
  FaceScanFunnel.prototype.applyAgeConsumers = function (age) {
    if (this.snapAge) this.snapAge.textContent = String(age);   // single source share image + Klaviyo read
    if (this.realAge != null) this.setAge(this.realAge);
    if (this.config.showTrajectory) this.renderTrajectory();
    this.refreshDuel();
    this.updateBridgeChip();
    this.updateOfferChip();
  };

  // Reveal the held number (remove the analysing loader). Idempotent. Marks the age as
  // revealed so the snapshot CTA can unlock exactly when there is a real number to capture.
  FaceScanFunnel.prototype.revealNumber = function () {
    if (this._analyzeTimer) { window.clearTimeout(this._analyzeTimer); this._analyzeTimer = null; }
    if (this._warmTimer) { window.clearTimeout(this._warmTimer); this._warmTimer = null; }
    // Only count as "revealed" (which unlocks the snapshot CTA + freezes the share frame)
    // when a REAL number is on screen — never over a "--". The degraded compound failure
    // (on-device age model down AND cloud down) then falls through to the engine-lock /
    // safety-valve path instead of freezing + sharing a "--" as the user's skin age.
    if (this.visualAge() != null) this._ageRevealed = true;
    if (this.live) this.live.classList.remove('is-analyzing');
  };

  FaceScanFunnel.prototype.setAuthoritativeAge = function (age) {
    var self = this;
    // Freeze the engine FIRST so its analyze() loop can't overwrite the number mid-animation.
    if (this.engine) this.engine.resultLocked = true;
    age = Math.round(age);
    if (this.live && this.live.classList.contains('is-analyzing')) {
      // number was hidden behind the analysing loader — set it and reveal at the ACCURATE value
      // (no on-device→cloud morph-down, which is the "too old first" the owner flagged).
      if (this.ageNode) this.ageNode.textContent = String(age);
      this.revealNumber();
      this.applyAgeConsumers(age);
    } else {
      // late correction after the number was already shown, or snapshot reuse → smooth morph
      this.animateAge(age, function () { self.applyAgeConsumers(age); });
    }
  };

  FaceScanFunnel.prototype.applyMivoloAge = function () {
    var self = this;
    if (!this.mivoloAgeUrl()) return;                       // feature off / not configured
    if (this._mivoloAge != null) { this.setAuthoritativeAge(this._mivoloAge); return; }  // reuse (e.g. at snapshot)
    if (this._mivoloBusy) return;
    this._mivoloBusy = true;
    var gen = this._scanGen || 0;                           // snapshot the scan generation
    // The "analysing" loader (set in onResultReady) is the wait indicator now; a 10s fallback
    // there reveals the on-device number if the cloud is slow/down.
    var run = function (recapturesLeft) {
      return self.captureAgeBlob().then(function (blob) {
        return self.fetchMivoloAge(blob);
      }).then(function (res) {
        if ((self._scanGen || 0) !== gen) return;           // a restart happened mid-fetch → drop stale result
        if (res && res.error === 'no_face' && recapturesLeft > 0) {
          // bad instant (blink / face moved at capture) — one fresh frame after a beat
          return new Promise(function (r) { window.setTimeout(r, 1200); }).then(function () {
            if ((self._scanGen || 0) !== gen) return;
            return run(recapturesLeft - 1);
          });
        }
        self._mivoloBusy = false;
        if (!res || res.age == null) {
          self.revealNumber();                              // cloud failed → reveal the on-device estimate
          self.setRefine('');
          if (res && res.error && res.error !== 'unavailable') {
            track('fs_error', { fs_error_type: 'mivolo_' + res.error });
          }
          return;
        }
        self._mivoloAge = res.age;
        self.setAuthoritativeAge(res.age);
        self.setRefine('confirmed');
        window.setTimeout(function () { if ((self._scanGen || 0) === gen) self.setRefine(''); }, 2200);
        var lo = Math.floor(res.age / 10) * 10;             // coarse band only — never the exact number
        track('fs_mivolo_age', { fs_age_band: lo + '-' + (lo + 9) });
        self.pushOutcome();                                 // corrected band → Klaviyo re-identify
      });
    };
    run(1);
  };

  /* ---------- step 3: snapshot ---------- */
  FaceScanFunnel.prototype.enterShare = function () {
    if (!this._heroFrozen) this.freezeHeroFrame();   // fallback: freeze now if the lock path didn't
    this.drawSnapshot(this.snapCanvas);
    if (this.snapAge) this.snapAge.textContent = this.ageNode ? this.ageNode.textContent : '--';
    this.applyMivoloAge();
  };

  /* No-camera / denied fallback: analyse an uploaded selfie via the cloud age model
     (fetchMivoloAge accepts any File; the server does its own face detection), then flip
     the funnel into the result state manually — age-only, matching the live funnel. */
  FaceScanFunnel.prototype.applyUploadedSelfie = function (file) {
    var self = this, i18n = this.config.i18n || {};
    if (!file) return;
    if (!this.mivoloAgeUrl()) { this.setUploadStatus(i18n.uploadError || 'Upload not available.', true); return; }
    this.setUploadStatus(i18n.uploadBusy || 'Analysing …', false);
    var btn = this.root.querySelector('[data-fs-upload]');
    if (btn) btn.disabled = true;
    var done = function (err) { if (btn) btn.disabled = false; if (err) self.setUploadStatus(err, true); };
    var img = new Image();
    img.onload = function () {
      // downscale to 720px (same budget as captureAgeBlob) for a fast upload
      var longSide = Math.max(img.naturalWidth, img.naturalHeight) || 720;
      var scale = longSide > 720 ? 720 / longSide : 1;
      var w = Math.round((img.naturalWidth || 720) * scale), h = Math.round((img.naturalHeight || 720) * scale);
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(img, 0, 0, w, h);
      var send = function (blob) {
        self.fetchMivoloAge(blob || file).then(function (res) {
          if (!res || res.age == null) {
            done((res && res.error === 'no_face') ? (i18n.uploadNoFace || 'No face found — try another photo.') : (i18n.uploadError || 'Could not analyse that photo.'));
            track('fs_error', { fs_error_type: 'upload_' + ((res && res.error) || 'unknown') });
            return;
          }
          if (btn) btn.disabled = false;
          self.setUploadStatus('', false);
          self._uploadedImage = img;                 // snapshot source (un-mirrored)
          self._mivoloAge = res.age;                 // cached authoritative age
          var blk = self.root.querySelector('[data-face-scan-camera-block]');
          if (blk) blk.hidden = true;
          if (!self.resultReadyAt) { self.resultReadyAt = Date.now(); self.onResultReady(); }
          else { self.setAuthoritativeAge(res.age); }
          if (self.toShare) self.toShare.disabled = false;
          var statusEl = self.root.querySelector('[data-face-scan-app-status]');
          if (statusEl && i18n.resultPill) statusEl.textContent = i18n.resultPill;
          track('fs_upload_result', { fs_age_band: self.ageBand() });
        }).catch(function () { done(i18n.uploadError || 'Could not analyse that photo.'); });
      };
      if (cv.toBlob) cv.toBlob(send, 'image/jpeg', 0.85); else send(null);
    };
    img.onerror = function () { done(i18n.uploadError || 'Could not read that image.'); };
    img.src = URL.createObjectURL(file);
  };

  FaceScanFunnel.prototype.setUploadStatus = function (msg, isError) {
    var el = this.root.querySelector('[data-fs-upload-error]');
    if (!el) return;
    if (!msg) { el.hidden = true; el.textContent = ''; el.classList.remove('is-error'); return; }
    el.textContent = msg; el.hidden = false;
    if (isError) el.classList.add('is-error'); else el.classList.remove('is-error');
  };

  FaceScanFunnel.prototype.drawCover = function (ctx, src, x, y, w, h, mirror) {
    // src may be a <video> (videoWidth) or an uploaded <img> (naturalWidth) — read both.
    var sW = src.videoWidth || src.naturalWidth || src.width, sH = src.videoHeight || src.naturalHeight || src.height;
    if (!sW || !sH) return;
    var vr = sW / sH, cr = w / h, sw, sh, sx, sy;
    if (vr > cr) { sh = sH; sw = sh * cr; sx = (sW - sw) / 2; sy = 0; }
    else { sw = sW; sh = sw / cr; sx = 0; sy = (sH - sh) / 2; }
    ctx.save();
    if (mirror) { ctx.translate(x + w, y); ctx.scale(-1, 1); ctx.drawImage(src, sx, sy, sw, sh, 0, 0, w, h); }
    else { ctx.drawImage(src, sx, sy, sw, sh, x, y, w, h); }
    ctx.restore();
  };

  // The image behind the snapshot. Priority: the uploaded selfie (un-mirrored) → the FROZEN
  // hero frame captured when the result locked (mirrored, like the preview) → the live video
  // as a last resort. Freezing means the shared image is the pictured result, not a fresh
  // (often worse) capture taken at share time.
  FaceScanFunnel.prototype.snapSource = function () {
    if (this._uploadedImage && (this._uploadedImage.naturalWidth || this._uploadedImage.width)) return { el: this._uploadedImage, mirror: false };
    if (this._heroFrozen && this._heroFrame && this._heroFrame.width) return { el: this._heroFrame, mirror: true };
    var video = this.root.querySelector('[data-face-scan-video]');
    if (video && video.videoWidth) return { el: video, mirror: true };
    return null;
  };

  // Freeze the live frame at the moment the result locks (face settled, holding still), so the
  // on-screen snapshot card AND the shared image are the SAME good frame — not two fresh grabs.
  FaceScanFunnel.prototype.freezeHeroFrame = function () {
    var video = this.root.querySelector('[data-face-scan-video]');
    if (!video || !video.videoWidth) return;
    var longSide = Math.max(video.videoWidth, video.videoHeight);
    var scale = longSide > 1280 ? 1280 / longSide : 1;   // crisp for a share card, capped for memory
    var w = Math.round(video.videoWidth * scale), h = Math.round(video.videoHeight * scale);
    if (!this._heroFrame) this._heroFrame = document.createElement('canvas');
    this._heroFrame.width = w; this._heroFrame.height = h;
    this._heroFrame.getContext('2d').drawImage(video, 0, 0, w, h);
    // capture the face landmarks (normalized to the video) for the scan-mesh overlay —
    // best-effort: MediaPipe gives 468 points; the fallback detector gives none (no mesh).
    var lf = this.engine && this.engine.latestFrame;
    this._heroLandmarks = (lf && lf.points && lf.points.length) ? lf.points.map(function (p) { return { x: p.x, y: p.y }; }) : null;
    this._heroFrozen = true;
  };

  /* Subtle "scanned" overlay: a constellation of the analysed face points, cover-cropped and
     mirrored to match the photo exactly. Purely visual (no measurement claim). No-op without
     landmarks (fallback detector / uploaded photo). */
  FaceScanFunnel.prototype.drawFaceMesh = function (ctx, srcW, srcH, x, y, w, h, mirror) {
    var pts = this._heroLandmarks;
    if (!pts || !pts.length || !srcW || !srcH) return;
    // replicate drawCover's cover-crop so the dots land on the same pixels as the photo
    var cr = w / h, vr = srcW / srcH, sw, sh, sx, sy;
    if (vr > cr) { sh = srcH; sw = sh * cr; sx = (srcW - sw) / 2; sy = 0; }
    else { sw = srcW; sh = sw / cr; sx = 0; sy = (srcH - sh) / 2; }
    var r = Math.max(1.3, w / 660);
    ctx.save();
    ctx.fillStyle = 'rgba(255,247,237,0.5)';
    for (var i = 0; i < pts.length; i++) {
      var vx = pts[i].x * srcW, vy = pts[i].y * srcH;
      if (vx < sx || vx > sx + sw || vy < sy || vy > sy + sh) continue;
      var u = (vx - sx) / sw, v = (vy - sy) / sh;
      var cx = x + (mirror ? (1 - u) : u) * w, cy = y + v * h;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, 6.2832); ctx.fill();
    }
    ctx.restore();
  };

  FaceScanFunnel.prototype.drawSnapshot = function (cv) {
    if (!cv) return;
    var src = this.snapSource();
    var w = cv.clientWidth || 420, h = cv.clientHeight || 300;
    cv.width = w; cv.height = h;
    var ctx = cv.getContext('2d');
    if (src) {
      this.drawCover(ctx, src.el, 0, 0, w, h, src.mirror);
      this.drawFaceMesh(ctx, src.el.videoWidth || src.el.naturalWidth || src.el.width, src.el.videoHeight || src.el.naturalHeight || src.el.height, 0, 0, w, h, src.mirror);
    }
    else { ctx.fillStyle = '#241009'; ctx.fillRect(0, 0, w, h); }
  };

  function roundRectPath(ctx, x, y, w, h, r) {
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return; }
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  /* Branded share image (portrait, integrated): the face + the verdict are ONE image —
     age overlaid on the photo over a scrim, a challenge band, and a legible URL strip.
     JPEG (photographic, no transparency) → ~10x smaller than PNG, faster share sheet,
     avoids the upload-timeout path. Display face = ZellGrotesk Condensed (loaded first). */
  FaceScanFunnel.prototype.buildShareImage = function () {
    var self = this;
    var DISPLAY = '"ZellGrotesk Condensed", "ZellGroteskCondensed", Saans, system-ui, sans-serif';
    var draw = function (resolve) {
      var W = 1080, H = 1350, bandH = 150, stripH = 96, photoH = H - bandH - stripH;
      var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      var ctx = cv.getContext('2d');
      ctx.fillStyle = '#241009'; ctx.fillRect(0, 0, W, H);
      var snap = self.snapSource();
      if (snap) {
        self.drawCover(ctx, snap.el, 0, 0, W, photoH, snap.mirror);
        self.drawFaceMesh(ctx, snap.el.videoWidth || snap.el.naturalWidth || snap.el.width, snap.el.videoHeight || snap.el.naturalHeight || snap.el.height, 0, 0, W, photoH, snap.mirror);
      }
      // bottom scrim so the age is legible over any photo
      var grad = ctx.createLinearGradient(0, photoH - 620, 0, photoH);
      grad.addColorStop(0, 'rgba(20,8,4,0)'); grad.addColorStop(1, 'rgba(20,8,4,.86)');
      ctx.fillStyle = grad; ctx.fillRect(0, photoH - 620, W, 620);
      // wordmark, top-left
      ctx.textBaseline = 'top'; ctx.textAlign = 'left';
      ctx.fillStyle = '#FFF7ED'; ctx.font = '700 44px Saans, system-ui, sans-serif';
      ctx.fillText('ZELLAFORTE', 56, 52);
      // label + age ON the photo
      var labelEl = self.root.querySelector('.fs-snapcard__label');
      var label = labelEl ? labelEl.textContent.toUpperCase() : 'MY SKIN AGE';
      var age = (self.ageNode ? self.ageNode.textContent.trim() : '--') || '--';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = 'rgba(255,247,237,.85)'; ctx.font = '600 32px Saans, system-ui, sans-serif';
      ctx.fillText(label, 60, photoH - 300);
      ctx.fillStyle = '#FFF7ED'; ctx.font = '800 320px ' + DISPLAY;
      ctx.fillText(age, 52, photoH - 60);
      var ageW = ctx.measureText(age).width;
      // age-gap brag pill next to the number (duel hook travels with the image)
      var gapEl = self.snapGap;
      if (gapEl && !gapEl.hidden && gapEl.textContent.trim()) {
        var gapTxt = gapEl.textContent.trim();
        var younger = gapEl.hasAttribute('data-younger');
        ctx.font = '700 46px Saans, system-ui, sans-serif';
        var gw = ctx.measureText(gapTxt).width + 60;
        var gx = 60 + ageW + 30, gy = photoH - 220;
        ctx.fillStyle = younger ? '#37B26A' : '#FFF7ED';
        roundRectPath(ctx, gx, gy, gw, 78, 39); ctx.fill();
        ctx.fillStyle = younger ? '#ffffff' : '#431207';
        ctx.textBaseline = 'middle'; ctx.fillText(gapTxt, gx + 30, gy + 41);
        ctx.textBaseline = 'alphabetic';
      }
      // orange challenge band
      ctx.fillStyle = '#FA660A'; ctx.fillRect(0, photoH, W, bandH);
      var chEl = self.root.querySelector('[data-fs-snap-challenge]');
      var challenge = chEl && chEl.textContent.trim() ? chEl.textContent.trim() : 'What\'s your skin age?';
      var size = 62; ctx.fillStyle = '#FFF7ED'; ctx.textBaseline = 'middle';
      ctx.font = '800 ' + size + 'px ' + DISPLAY;
      while (ctx.measureText(challenge).width > W - 112 && size > 34) { size -= 2; ctx.font = '800 ' + size + 'px ' + DISPLAY; }
      ctx.fillText(challenge, 56, photoH + bandH / 2 + 2);
      // cream URL strip (destination survives feed compression)
      ctx.fillStyle = '#FFF7ED'; ctx.fillRect(0, photoH + bandH, W, stripH);
      ctx.fillStyle = '#431207'; ctx.font = '700 42px Saans, system-ui, sans-serif';
      ctx.fillText('zellaforte.de', 56, photoH + bandH + stripH / 2 + 2);
      ctx.textBaseline = 'alphabetic';
      if (cv.toBlob) cv.toBlob(function (b) { resolve(b); }, 'image/jpeg', 0.9);
      else resolve(null);
    };
    return new Promise(function (resolve) {
      if (document.fonts && document.fonts.load) {
        document.fonts.load('800 100px "ZellGrotesk Condensed"').then(function () { draw(resolve); }, function () { draw(resolve); });
      } else { draw(resolve); }
    });
  };

  /* The post-share note, honestly resolved: only promise the Duell discount when a
     real code is configured ({code} replaced); otherwise a promise-free thank-you.
     Used for BOTH the share-step note and the modal note — never a raw placeholder. */
  FaceScanFunnel.prototype.duellNoteText = function (el) {
    if (!el) return '';
    var code = (this.config.duellCode || '').trim();
    if (code) {
      var pct = Math.max(0, Math.min(90, parseInt(this.config.duellPct, 10) || 0));
      return (el.getAttribute('data-unlocked') || '').replace('{code}', code).replace('{pct}', String(pct));
    }
    return el.getAttribute('data-thanks') || '';
  };

  FaceScanFunnel.prototype.markShared = function () {
    if (this.shareBtn) {
      this.shareBtn.disabled = false;
      this.shareBtn.textContent = this.shareBtn.getAttribute('data-done') || this.shareBtn.textContent;
    }
    if (this.shareNote) {
      var note = this.duellNoteText(this.shareNote);
      if (note) { this.shareNote.textContent = note; this.shareNote.setAttribute('data-shared', ''); }
    }
    // Sharing earns the Duell pricing ("ihr beide") — re-render the Kur box with it.
    if (!this.shared) { this.shared = true; this.renderKur(); }
  };

  /* Upload with a 10s timeout and one retry — a hung fetch must not strand the share flow. */
  FaceScanFunnel.prototype.uploadWithTimeout = function (eng, blob) {
    function attempt() {
      return Promise.race([
        eng.uploadShareResult(blob),
        new Promise(function (resolve, reject) { window.setTimeout(function () { reject(new Error('upload timeout')); }, 10000); })
      ]);
    }
    return attempt().catch(function () { return attempt(); });
  };

  FaceScanFunnel.prototype.share = function () {
    var self = this;
    var eng = this.getEngine();
    if (this.shareBtn) {
      this.shareBtn.disabled = true;
      var sharing = this.shareBtn.getAttribute('data-sharing');
      if (sharing) this.shareBtn.textContent = sharing;
    }
    this.buildShareImage().then(function (blob) {
      self.shareBlob = blob;
      var method = 'modal';
      var urlP;
      if (blob && eng && typeof eng.uploadShareResult === 'function') {
        urlP = self.uploadWithTimeout(eng, blob).then(function (res) {
          return (res && (res.shareUrl || (eng.buildResultPageUrl && eng.buildResultPageUrl(res.id)))) || self.fallbackUrl(eng);
        }).catch(function () {
          track('fs_error', { fs_error_type: 'upload_failed' });
          return self.fallbackUrl(eng);
        });
      } else {
        urlP = Promise.resolve(self.fallbackUrl(eng));
      }
      urlP.then(function (url) {
        self.shareUrl = url || '';
        self.markShared();
        var file = (typeof File !== 'undefined' && blob) ? new File([blob], 'zellaforte-hautalter.jpg', { type: 'image/jpeg' }) : null;
        var canNative = COARSE && navigator.share && file && (!navigator.canShare || navigator.canShare({ files: [file] }));
        method = canNative ? 'native_sheet' : 'modal';
        track('fs_share_created', { fs_share_method: method });
        if (canNative) {
          navigator.share({ files: [file], title: 'Zellaforte', text: self.shareText(), url: self.shareUrl })
            .catch(function (e) { if (!e || e.name !== 'AbortError') self.openShareModal(); });
        } else {
          self.openShareModal();
        }
      });
    });
  };

  FaceScanFunnel.prototype.fallbackUrl = function (eng) {
    if (eng && typeof eng.buildScanReferralUrl === 'function') { try { return eng.buildScanReferralUrl(); } catch (e) {} }
    return this.config.kbUrl || window.location.href;
  };

  /* Outgoing share caption — curiosity, not a challenge. Injects the real age so the
     recipient wants to measure their own ("My skin age is 28 👀 What's yours?"). Locale
     drives the language; falls back to the age-less line if the number isn't ready. */
  FaceScanFunnel.prototype.shareText = function () {
    var i18n = this.config.i18n || {};
    var raw = this.ageNode ? (this.ageNode.textContent || '').replace(/[^\d.]/g, '') : '';
    var n = parseFloat(raw);
    var text;
    if (isFinite(n) && n > 0) {
      text = (i18n.shareCaption || 'My skin age is %age% 👀 What\'s yours? Find out 👉').replace('%age%', String(Math.round(n)));
    } else {
      text = i18n.shareCaptionPlain || 'What\'s your skin age? 👀 Find out 👉';
    }
    return text + ' ';
  };

  FaceScanFunnel.prototype.openShareModal = function () {
    if (!this.shareModal) return;
    if (this.modalNote) {
      var note = this.duellNoteText(this.modalNote);
      this.modalNote.textContent = note;
      this.modalNote.hidden = !note;
    }
    if (this.sharePreview && this.shareBlob) {
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = URL.createObjectURL(this.shareBlob);
      this.sharePreview.src = this.previewObjectUrl;
    }
    var url = this.shareUrl || '';
    var text = this.shareText();
    if (this.shareUrlEl) this.shareUrlEl.textContent = url;
    if (this.shareWhatsapp) this.shareWhatsapp.href = 'https://wa.me/?text=' + encodeURIComponent(text + url);
    if (this.shareEmail) this.shareEmail.href = 'mailto:?subject=' + encodeURIComponent('Zellaforte') + '&body=' + encodeURIComponent(text + url);
    if (this.shareX) this.shareX.href = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(text) + '&url=' + encodeURIComponent(url);
    this.shareModal.hidden = false;
    this.shareModal.classList.add('is-open');
  };

  FaceScanFunnel.prototype.closeShareModal = function () {
    if (!this.shareModal) return;
    this.shareModal.classList.remove('is-open');
    this.shareModal.hidden = true;
    if (this.previewObjectUrl) { URL.revokeObjectURL(this.previewObjectUrl); this.previewObjectUrl = ''; }
  };

  FaceScanFunnel.prototype.copyShareLink = function () {
    var self = this; var eng = this.engine; var url = this.shareUrl || '';
    var done = function () {
      if (self.shareCopyBtn) {
        var label = self.shareCopyBtn.textContent;
        self.shareCopyBtn.textContent = '✓';
        window.setTimeout(function () { if (self.shareCopyBtn) self.shareCopyBtn.textContent = label; }, 1600);
      }
    };
    if (eng && typeof eng.copyShareUrl === 'function') { eng.copyShareUrl(url); done(); return; }
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done); else done();
  };

  FaceScanFunnel.prototype.downloadBlob = function (blob, name) {
    var eng = this.engine;
    if (eng && typeof eng.downloadBlob === 'function') { eng.downloadBlob(blob, name); return; }
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  };

  /* ---------- step 4: gut-skin ---------- */
  FaceScanFunnel.prototype.enterBridge = function () {
    /* the camera has done its job (snapshot drawn on the share step) — release it so the
       OS camera indicator goes off while the user reads the pitch; restart() re-opens it */
    if (this.engine && typeof this.engine.stopCamera === 'function') {
      try { this.engine.stopCamera(); } catch (e) {}
    }
    /* personalize: pull the scanned visual age into the story (the axis journey
       itself is CSS/SMIL-driven; the study graph lives on the offer step) */
    this.updateBridgeChip();
  };

  // Separate so a LATE MiVOLO correction (25s budget) landing while the user already
  // reads the bridge can re-template the chip instead of contradicting the hero.
  FaceScanFunnel.prototype.updateBridgeChip = function () {
    var chip = this.root.querySelector('[data-fs-bridge-age]');
    if (!chip) return;
    var age = this._mivoloAge != null ? this._mivoloAge : this.visualAge();
    if (!age) { chip.hidden = true; return; }
    chip.textContent = (chip.getAttribute('data-tpl') || '').replace('{age}', age);
    chip.hidden = false;
  };

  // Offer hero mirrors the bridge chip: restate the scanned age at the buy decision
  // (message-match). Same age source; a LATE MiVOLO correction re-templates it via
  // setAuthoritativeAge. Restate only — never chained to a product outcome (HCVO firewall).
  FaceScanFunnel.prototype.updateOfferChip = function () {
    var chip = this.root.querySelector('[data-fs-offer-age]');
    if (!chip) return;
    var age = this._mivoloAge != null ? this._mivoloAge : this.visualAge();
    if (!age) { chip.hidden = true; return; }
    chip.textContent = (chip.getAttribute('data-tpl') || '').replace('{age}', age);
    chip.hidden = false;
  };

  /* ---------- step 5 reveal: study graph count-up (graph moved here from bridge) ---------- */
  FaceScanFunnel.prototype.enterOffer = function () {
    var self = this;
    this.updateOfferChip();
    var graph = this.root.querySelector('[data-fs-graph]');
    if (!graph || graph.classList.contains('is-animated')) return;
    if (REDUCED) { graph.classList.add('is-animated'); this.countUpBars(); return; }
    window.setTimeout(function () { graph.classList.add('is-animated'); self.countUpBars(); }, 160);
  };

  FaceScanFunnel.prototype.countUpBars = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fs-bar]'), function (bar) {
      var numEl = bar.querySelector('[data-fs-bar-num]');
      if (!numEl) return;
      var target = parseInt(bar.getAttribute('data-val'), 10) || 0;
      var sign = bar.getAttribute('data-sign') || '+';
      if (REDUCED) { numEl.textContent = sign + target; return; }
      var start = performance.now(), dur = 1100;
      var tick = function (now) {
        var p = Math.min(1, (now - start) / dur);
        numEl.textContent = sign + Math.round((1 - Math.pow(1 - p, 3)) * target);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  };

  /* Product media gallery: one active slide, thumbs + swipe both land here. */
  FaceScanFunnel.prototype.setGallery = function (i) {
    var n = this.gallerySlides.length;
    if (!n) return;
    i = Math.max(0, Math.min(n - 1, isNaN(i) ? 0 : i));
    this.galleryIndex = i;
    this.gallerySlides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
    this.galleryThumbs.forEach(function (t, idx) { t.classList.toggle('is-active', idx === i); });
  };

  /* ---------- step 5: offer — Kur buy-box ----------
     One-time Kur packs (qty × base variant) in the product-page buybox design.
     Prices are computed from the live variant price so display always matches
     the checkout math; per-tier discount codes come from section settings and
     the billing line always NAMES the code it expects (§312j transparency). */

  FaceScanFunnel.prototype.money = function (cents) {
    var v = (Math.round(cents) / 100).toFixed(2).replace('.', ',');
    if (v.slice(-3) === ',00') v = v.slice(0, -3);
    return '€' + v;
  };

  /* Effective pricing per tier: the Duell code replaces a tier's own code whenever
     the Duell is active AND strictly better (one code per checkout). pct is clamped —
     a fat-fingered setting must never produce a €0 display. */
  FaceScanFunnel.prototype.effectiveTier = function (key) {
    var t = (this.config.tiers || {})[key];
    if (!t) return null;
    var qty = Math.max(1, parseInt(t.qty, 10) || 1);
    var pct = Math.max(0, Math.min(90, parseInt(t.pct, 10) || 0));
    var code = (t.code || '').trim();
    if (this.duellActive()) {
      var dPct = Math.max(0, Math.min(90, parseInt(this.config.duellPct, 10) || 0));
      if (dPct > pct) { pct = dPct; code = (this.config.duellCode || '').trim(); }
    }
    var full = (parseInt(this.config.baseCents, 10) || 5900) * qty;
    var total = Math.round(full * (100 - pct) / 100);
    // perDay: 1 jar = 60 caps at 2/day = 30 days, so days = qty * 30. Makes the
    // daily-ritual price feel small; identical across tiers unless a tier is discounted.
    return { key: key, qty: qty, pct: pct, code: code, full: full, total: total, perMonth: Math.round(total / qty), perDay: Math.round(total / (qty * 30)) };
  };

  FaceScanFunnel.prototype.renderKur = function () {
    var self = this;
    if (!this.kurTabs || !this.kurTabs.length) return;
    var i18n = this.config.i18n || {};
    this.kurTabs.forEach(function (tab) {
      var key = tab.getAttribute('data-fs-kur');
      var t = self.effectiveTier(key);
      if (!t) { tab.hidden = true; return; }
      var pm = tab.querySelector('[data-fs-kur-pm]');
      var save = tab.querySelector('[data-fs-kur-save]');
      if (pm) pm.textContent = self.money(t.perDay) + (i18n.perDay || ' / day');
      if (save) { save.textContent = '−' + t.pct + '%'; save.hidden = t.pct <= 0; }
      if (key === 'm3' && i18n.recommended && !tab.querySelector('.fs-kbx__reco')) {
        var reco = document.createElement('span');
        reco.className = 'fs-kbx__reco';
        reco.textContent = i18n.recommended;
        tab.appendChild(reco);
      }
    });
    this.selectTier(this.tier, false);
  };

  FaceScanFunnel.prototype.selectTier = function (key, fromUser) {
    var t = this.effectiveTier(key);
    if (!t) return;
    this.tier = key;
    this.kurTabs.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-fs-kur') === key); });
    var i18n = this.config.i18n || {};
    if (this.kurTotal) this.kurTotal.textContent = this.money(t.total);
    if (this.kurAnchor) {
      // struck full price only when there's a real discount — t.full is the genuine
      // undiscounted price this same seller charges, so the anchor is never fabricated.
      if (t.pct > 0) { this.kurAnchor.textContent = this.money(t.full); this.kurAnchor.hidden = false; }
      else { this.kurAnchor.hidden = true; }
    }
    if (this.kurPill) {
      if (t.pct > 0) { this.kurPill.textContent = '−' + t.pct + '%'; this.kurPill.hidden = false; }
      else { this.kurPill.hidden = true; }
    }
    if (this.kurDoses) this.kurDoses.textContent = (i18n.doses || '').replace('{n}', t.qty);
    if (this.ctaPrice) this.ctaPrice.textContent = '· ' + this.money(t.total);
    if (this.kurBilling) {
      // Code names stay in the checkout plumbing only — the UI speaks in percent
      // ("applied automatically"), never in coupon jargon.
      var codeTpl = this.kurBilling.getAttribute('data-code-tpl') || '';
      var plainTpl = this.kurBilling.getAttribute('data-plain-tpl') || '';
      this.kurBilling.textContent = (t.code && t.pct > 0)
        ? codeTpl.replace('{total}', this.money(t.total)).replace('{pct}', String(t.pct))
        : this.money(t.total) + ' · ' + plainTpl;
    }
    if (this.duellChip && this.duellChipText) {
      // Only when the Duell actually improves THIS tier's price — and without
      // exposing a second code next to the billing line.
      var duellCode = (this.config.duellCode || '').trim();
      var chipOn = this.duellActive() && !!duellCode && t.code === duellCode;
      this.duellChip.hidden = !chipOn;
      if (chipOn) this.duellChipText.textContent = (i18n.duellChip || '').replace('{pct}', String(t.pct));
    }
    if (this.stickyLabel) {
      var tab = this.root.querySelector('[data-fs-kur="' + key + '"] .fs-kbx__tab-title');
      this.stickyLabel.textContent = (tab ? tab.textContent.trim() + ' · ' : '') + this.money(t.total);
    }
    if (fromUser) track('fs_kur_select', { fs_plan: key, fs_pct: t.pct });
  };

  FaceScanFunnel.prototype.addToCart = function () {
    var self = this;
    var variantId = this.config.kbVariantId;
    var t = this.effectiveTier(this.tier);
    if (!this.config.enableCart || !variantId || !t) {
      track('fs_offer_exit_to_product', { fs_plan: this.tier });
      if (this.config.kbUrl) window.location.href = this.config.kbUrl;
      return;
    }
    if (this.addBtn) this.addBtn.disabled = true;
    track('fs_add_to_cart', { fs_plan: this.tier, fs_qty: t.qty, fs_variant_id: String(variantId) });
    metaTrack('AddToCart', { content_ids: [String(variantId)], content_type: 'product', value: t.total / 100, currency: 'EUR', num_items: t.qty });
    // /cart/update.js SETS the line quantity (idempotent) — a back-navigation and
    // second click can never double the Kur, and other cart items are preserved.
    var updates = {}; updates[String(variantId)] = t.qty;
    fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (res) { if (!res.ok) throw new Error('cart'); return res.json(); })
      .then(function () {
        track('fs_begin_checkout', { fs_plan: self.tier, fs_discount_code: t.code || '' });
        metaTrack('InitiateCheckout', { content_ids: [String(variantId)], content_type: 'product', value: t.total / 100, currency: 'EUR', num_items: t.qty });
        var dest = '/checkout';
        window.location.href = t.code ? ('/discount/' + encodeURIComponent(t.code) + '?redirect=' + encodeURIComponent(dest)) : dest;
      }).catch(function () {
        track('fs_error', { fs_error_type: 'cart_add_failed' });
        if (self.addBtn) self.addBtn.disabled = false;
        if (self.config.kbUrl) window.location.href = self.config.kbUrl;
      });
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-fs-funnel]').forEach(function (root) {
      if (!root.__faceScanFunnel) root.__faceScanFunnel = new FaceScanFunnel(root);
    });
  });
})();
